// Seperated to ensure this is not imported
import type { SafeParseError, SafeParseReturnType } from "zod";
import { LAMValidationError, dispatchError } from "./validationUtils";
import {
  BaseLAM2,
  EVENT_CATEGORY_2,
  EVENT_SUBTYPE_2,
  EVENT_TYPE_2,
} from "@lululemon/data-collection-types";
import { deepCopy, MissingFederatedModule } from "../../generalUtils";

import {
  VALIDATION_MODULE,
  VALIDATION_MODULE_ALT_NAME,
} from "../../generalUtils/consts";

import { pushToLAM } from "./transformation";
import { pathMutator } from "./transformationEngineV2/transformationEngine";

const LAM2_TRANSFORMATIONS = "Automated Transformations";

export function validateLAMForEvent<
  Input extends BaseLAM2,
  ETR extends Partial<(typeof EVENT_TYPE_2)[keyof typeof EVENT_TYPE_2]> =
    undefined,
  ET extends Partial<(typeof EVENT_SUBTYPE_2)[keyof typeof EVENT_SUBTYPE_2]> =
    undefined,
  EST extends Partial<
    (typeof EVENT_CATEGORY_2)[keyof typeof EVENT_CATEGORY_2]
  > = undefined,
  Instance extends { dispatch: (...args: any) => Promise<void> } = {
    dispatch: (...args: any) => Promise<void>;
  },
>(
  instance: Instance,
  lam: Input,
  v2?: {
    eventType: ETR;
    eventSubType: ET;
    eventCategory: EST;
  }
): SafeParseReturnType<Input, Input> | undefined {
  if (
    self[VALIDATION_MODULE] === undefined &&
    self[VALIDATION_MODULE_ALT_NAME] === undefined
  ) {
    // Here since module is not loaded no access to zod
    return {
      success: false,
      // @ts-ignore
      error: new MissingFederatedModule(VALIDATION_MODULE),
    };
  }

  if (v2 === undefined) {
    throw new Error("No LAM Config passed");
  }

  const eventName = `${v2.eventType}.${v2.eventSubType}.${v2.eventCategory}`;

  const validationModule =
    self[VALIDATION_MODULE] || self[VALIDATION_MODULE_ALT_NAME];
  let validator;
  try {
    // Will be a slight waste
    validator =
      validationModule.automatedValidatorObjects.schemaObjects[v2.eventType][
        v2.eventSubType
      ][v2.eventCategory];
  } catch (e) {
    console.error(e);
    throw e;
  }

  if (!validator) {
    const err = new Error(
      `[${LAM2_TRANSFORMATIONS}]: Event [${eventName}] does not have a validator`
    );
    dispatchError(instance, err);

    // To ensure that error is handled correctly by formatter utilize ZodError
    // To not import all of zod, at-least immediately, within mwa-analytics relying on federated module function instead
    const zodError = validationModule.createZodError([
      {
        code: "invalid_arguments",
        argumentsError: validationModule.createZodError([
          {
            code: "invalid_string",
            path: ["properties", "eventCategory"],
            validation: "cuid",
            message: err.message,
          },
        ]),
        message: err.message,
        path: ["properties", "eventCategory"],
      },
      {
        code: "invalid_arguments",
        argumentsError: validationModule.createZodError([
          {
            code: "invalid_string",
            path: ["properties", "eventSubType"],
            validation: "cuid",
            message: err.message,
          },
        ]),
        message: err.message,
        path: ["properties", "eventSubType"],
      },
      {
        code: "invalid_arguments",
        argumentsError: validationModule.createZodError([
          {
            code: "invalid_string",
            path: ["properties", "eventType"],
            validation: "cuid",
            message: err.message,
          },
        ]),
        message: err.message,
        path: ["properties", "eventType"],
      },
    ]);

    return {
      success: false,
      error: zodError,
    };
  }

  const validationResult = validator.safeParse(lam);
  if (!validationResult.success) {
    dispatchError(
      instance,
      new LAMValidationError(eventName, validationResult)
    );
    return validationResult as SafeParseError<Input>;
  }

  return {
    success: true,
    data: lam,
  };
}

export function getLAM2<
  ETR extends Partial<(typeof EVENT_TYPE_2)[keyof typeof EVENT_TYPE_2]> =
    undefined,
  ET extends Partial<(typeof EVENT_SUBTYPE_2)[keyof typeof EVENT_SUBTYPE_2]> =
    undefined,
  EST extends Partial<
    (typeof EVENT_CATEGORY_2)[keyof typeof EVENT_CATEGORY_2]
  > = undefined,
>(
  version: {
    eventType: ETR;
    eventSubType: ET;
    eventCategory: EST;
  },
  params?: Parameters<ReturnType<typeof pushToLAM>>[0]
) {
  if (version === undefined) {
    throw new Error("Version is undefined");
  }

  if (
    version.eventType === undefined ||
    version.eventCategory === undefined ||
    version.eventSubType === undefined
  ) {
    throw new Error("LAM version config object is missing data");
  }

  const { eventType, eventSubType, eventCategory } = version;
  const validationModule =
    self[VALIDATION_MODULE] || self[VALIDATION_MODULE_ALT_NAME];
  let schema;
  let validationFn;
  try {
    schema =
      validationModule.automatedValidatorObjects.defaultSchemas[eventType][
        eventSubType
      ][eventCategory];
    validationFn =
      validationModule.automatedValidatorObjects.schema[eventType][
        eventSubType
      ][eventCategory];
  } catch (e) {
    // TODO: Parse error to throw correct erorr
    throw new MissingFederatedModule(VALIDATION_MODULE);
  }

  if (schema === undefined || validationFn === undefined) {
    throw new Error("Validation or Schema Function is missing");
  }

  if (params !== undefined) {
    params.payload.properties.eventCategoryName = eventType;
    const schemaInstance = schema();
    params.payload.properties.pathMutatorObject = pathMutator(
      deepCopy(schemaInstance),
      schemaInstance
    );
  }

  return {
    schema,
    validationFn,
  };
}
